import React from "react";
import { withStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  CircularProgress,
  Snackbar,
  Switch,
  FormControlLabel,
  Box,
  Divider,
  Slider,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import utils from "../../universal/utils";

const styles = (theme) => ({
  root: {
    width: "100%",
  },
  section: {
    marginBottom: theme.spacing(4),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  splitCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  presetButtons: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginBottom: theme.spacing(3),
  },
  presetButton: {
    minWidth: 110,
  },
  sliderRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(3),
  },
  slider: {
    flex: 1,
  },
  sliderLabel: {
    minWidth: 100,
    fontWeight: 500,
  },
  saveSplitButton: {
    marginTop: theme.spacing(2),
  },
  splitDescription: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  addButton: {
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    maxHeight: 520,
  },
  activeChip: {
    backgroundColor: "#4caf50",
    color: "white",
  },
  inactiveChip: {
    backgroundColor: "#f44336",
    color: "white",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  dialogContent: {
    minWidth: 480,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  productOption: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
  },
  productImage: {
    width: 48,
    height: 48,
    objectFit: "contain",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
  },
  productDetails: {
    flex: 1,
  },
  productName: {
    fontWeight: 500,
    fontSize: "0.88rem",
  },
  productMeta: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
});

const hebrewLabels = {
  splitSection: "הגדרת חלוקה – אשף AI מול פרומו מכירות",
  splitDescription: (pct) => {
    if (pct === 0) return "כל ביקורי הדף מציגים את אשף ה-AI";
    if (pct === 100) return "כל ביקורי הדף מציגים את פרומו המכירות";
    return `${pct}% מביקורי הדף מציגים את פרומו המכירות, ${100 - pct}% מציגים את אשף ה-AI`;
  },
  presets: {
    wizardOnly: "אשף בלבד (0%)",
    saleOnly: "פרומו בלבד (100%)",
    fifty: "50/50",
    seventy: "70% פרומו",
  },
  saveSplit: "שמור הגדרת חלוקה",
  promosSection: "ניהול פרומואים",
  addPromo: "הוסף פרומו חדש",
  table: {
    product: "מוצר",
    title: "כותרת פרומו",
    cta: "טקסט כפתור",
    sortOrder: "סדר",
    active: "סטטוס",
    actions: "פעולות",
  },
  dialog: {
    create: "יצירת פרומו חדש",
    edit: "עריכת פרומו",
  },
  fields: {
    product: "בחר מוצר",
    title: "כותרת (תוצג מעל התמונה)",
    ctaText: 'טקסט כפתור (ברירת מחדל: "קנה עכשיו")',
    sortOrder: "סדר תצוגה (מספר נמוך = ראשון)",
    active: "פעיל",
  },
  buttons: {
    save: "שמור",
    cancel: "ביטול",
    delete: "מחיקה",
  },
  status: {
    active: "פעיל",
    inactive: "לא פעיל",
  },
  messages: {
    success: "הפעולה בוצעה בהצלחה",
    error: "אירעה שגיאה",
    loadingProducts: "טוען מוצרים...",
  },
  deleteConfirm: "האם למחוק פרומו זה?",
};

const SPLIT_PRESETS = [
  { label: hebrewLabels.presets.wizardOnly, value: 0 },
  { label: hebrewLabels.presets.fifty, value: 50 },
  { label: hebrewLabels.presets.seventy, value: 70 },
  { label: hebrewLabels.presets.saleOnly, value: 100 },
];

const basePath = utils.getEdgeBasePath();

const cdnPath = "https://s3.eu-west-2.amazonaws.com/sportyshop/";

function getProductImageUrl(product) {
  if (
    product.cdn &&
    Array.isArray(product.cdn) &&
    product.cdn[0] &&
    product.cdn[0].bucketPath &&
    product.cdn[0].bucketPath.indexOf(".jpeg") !== -1
  ) {
    return `${cdnPath}${product.cdn[0].bucketPath}`;
  }
  return `${basePath}/api/v1/media/product/${product.ean}`;
}

class HomepageSalesPromoManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // Split control
      salePercent: 0,
      pendingSalePercent: 0,
      savingPercent: false,

      // Promos
      promos: [],
      products: [],
      loading: true,
      loadingProducts: false,

      // Dialog
      dialogOpen: false,
      dialogMode: "create",
      currentPromo: {
        _id: null,
        ean: "",
        title: "",
        ctaText: "",
        sortOrder: 0,
        active: true,
      },
      selectedProduct: null,

      // Delete
      deleteDialogOpen: false,
      promoToDelete: null,

      snackbar: { open: false, message: "", severity: "success" },
    };
  }

  componentDidMount() {
    this.loadData();
    this.loadProducts();
  }

  loadData = async () => {
    this.setState({ loading: true });
    try {
      const response = await fetch(`${basePath}/api/v1/admin/homepage-promo`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await response.json();
      if (data.ok) {
        this.setState({
          salePercent: data.salePercent,
          pendingSalePercent: data.salePercent,
          promos: data.promos || [],
        });
      }
    } catch (err) {
      this.showSnackbar(hebrewLabels.messages.error, "error");
    } finally {
      this.setState({ loading: false });
    }
  };

  loadProducts = async () => {
    this.setState({ loadingProducts: true });
    try {
      const response = await fetch(`${basePath}/api/v1/admin/products/list`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await response.json();
      if (data.ok) {
        this.setState({ products: data.products || [] });
      }
    } catch (err) {
      console.error("Error loading products:", err);
    } finally {
      this.setState({ loadingProducts: false });
    }
  };

  // ─── Split control ──────────────────────────────────────────────────────────

  handlePresetClick = (value) => {
    this.setState({ pendingSalePercent: value });
  };

  handleSliderChange = (_, value) => {
    this.setState({ pendingSalePercent: value });
  };

  handleSaveSplit = async () => {
    const { pendingSalePercent } = this.state;
    this.setState({ savingPercent: true });
    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/homepage-promo/sale-percent`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...utils.getAdminAuthHeaders() },
          body: JSON.stringify({ salePercent: pendingSalePercent }),
        }
      );
      const data = await response.json();
      if (data.ok) {
        this.setState({ salePercent: data.salePercent, pendingSalePercent: data.salePercent });
        this.showSnackbar(hebrewLabels.messages.success, "success");
      } else {
        this.showSnackbar(hebrewLabels.messages.error, "error");
      }
    } catch (err) {
      this.showSnackbar(hebrewLabels.messages.error, "error");
    } finally {
      this.setState({ savingPercent: false });
    }
  };

  // ─── Promo CRUD ─────────────────────────────────────────────────────────────

  openCreateDialog = () => {
    this.setState({
      dialogOpen: true,
      dialogMode: "create",
      currentPromo: { _id: null, ean: "", title: "", ctaText: "", sortOrder: 0, active: true },
      selectedProduct: null,
    });
  };

  openEditDialog = (promo) => {
    const selectedProduct = this.state.products.find((p) => p.ean === promo.ean) || null;
    this.setState({
      dialogOpen: true,
      dialogMode: "edit",
      currentPromo: { ...promo },
      selectedProduct,
    });
  };

  closeDialog = () => {
    this.setState({ dialogOpen: false });
  };

  handleFieldChange = (field, value) => {
    this.setState((prev) => ({
      currentPromo: { ...prev.currentPromo, [field]: value },
    }));
  };

  handleProductChange = (_, product) => {
    this.setState((prev) => ({
      selectedProduct: product,
      currentPromo: { ...prev.currentPromo, ean: product ? product.ean : "" },
    }));
  };

  handleSave = async () => {
    const { dialogMode, currentPromo } = this.state;
    const { ean, title, ctaText, sortOrder, active } = currentPromo;

    if (!ean) {
      this.showSnackbar("נא לבחור מוצר", "warning");
      return;
    }
    if (!title || !title.trim()) {
      this.showSnackbar("נא להזין כותרת", "warning");
      return;
    }

    const payload = { ean, title: title.trim(), ctaText: ctaText.trim(), sortOrder: Number(sortOrder) || 0, active };

    try {
      let response;
      if (dialogMode === "create") {
        response = await fetch(`${basePath}/api/v1/admin/homepage-promo/promos`, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...utils.getAdminAuthHeaders() },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(
          `${basePath}/api/v1/admin/homepage-promo/promos/${currentPromo._id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json", ...utils.getAdminAuthHeaders() },
            body: JSON.stringify(payload),
          }
        );
      }
      const data = await response.json();
      if (data.ok) {
        this.closeDialog();
        this.showSnackbar(hebrewLabels.messages.success, "success");
        this.loadData();
      } else {
        this.showSnackbar(data.error || hebrewLabels.messages.error, "error");
      }
    } catch (err) {
      this.showSnackbar(hebrewLabels.messages.error, "error");
    }
  };

  handleToggleActive = async (promo) => {
    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/homepage-promo/promos/${promo._id}/toggle`,
        { method: "PATCH", headers: utils.getAdminAuthHeaders() }
      );
      const data = await response.json();
      if (data.ok) {
        this.loadData();
        this.showSnackbar(hebrewLabels.messages.success, "success");
      } else {
        this.showSnackbar(hebrewLabels.messages.error, "error");
      }
    } catch (err) {
      this.showSnackbar(hebrewLabels.messages.error, "error");
    }
  };

  openDeleteDialog = (promo) => {
    this.setState({ deleteDialogOpen: true, promoToDelete: promo });
  };

  handleDelete = async () => {
    const { promoToDelete } = this.state;
    this.setState({ deleteDialogOpen: false });
    try {
      const response = await fetch(
        `${basePath}/api/v1/admin/homepage-promo/promos/${promoToDelete._id}`,
        { method: "DELETE", headers: utils.getAdminAuthHeaders() }
      );
      const data = await response.json();
      if (data.ok) {
        this.showSnackbar(hebrewLabels.messages.success, "success");
        this.loadData();
      } else {
        this.showSnackbar(hebrewLabels.messages.error, "error");
      }
    } catch (err) {
      this.showSnackbar(hebrewLabels.messages.error, "error");
    }
  };

  // ─── Helpers ────────────────────────────────────────────────────────────────

  showSnackbar = (message, severity = "success") => {
    this.setState({ snackbar: { open: true, message, severity } });
  };

  closeSnackbar = () => {
    this.setState((prev) => ({ snackbar: { ...prev.snackbar, open: false } }));
  };

  formatProductLabel = (product) => {
    if (!product) return "";
    const parts = [product.brand, product.displayName, product.displaySize].filter(Boolean);
    return `${parts.join(" ")} (${product.ean})`;
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  render() {
    const { classes } = this.props;
    const {
      salePercent,
      pendingSalePercent,
      savingPercent,
      promos,
      products,
      loading,
      loadingProducts,
      dialogOpen,
      dialogMode,
      currentPromo,
      selectedProduct,
      deleteDialogOpen,
      snackbar,
    } = this.state;

    const splitChanged = pendingSalePercent !== salePercent;

    return (
      <Box className={classes.root}>

        {/* ── Split Control ─────────────────────────────────────────────────── */}
        <Paper className={classes.splitCard} elevation={1}>
          <Typography variant="h6" className={classes.sectionTitle}>
            {hebrewLabels.splitSection}
          </Typography>

          <Typography className={classes.splitDescription}>
            {hebrewLabels.splitDescription(pendingSalePercent)}
          </Typography>

          <Box className={classes.presetButtons}>
            {SPLIT_PRESETS.map((preset) => (
              <Button
                key={preset.value}
                className={classes.presetButton}
                variant={pendingSalePercent === preset.value ? "contained" : "outlined"}
                color="primary"
                size="small"
                onClick={() => this.handlePresetClick(preset.value)}
              >
                {preset.label}
              </Button>
            ))}
          </Box>

          <Box className={classes.sliderRow}>
            <Typography className={classes.sliderLabel}>
              {pendingSalePercent}% פרומו
            </Typography>
            <Slider
              className={classes.slider}
              value={pendingSalePercent}
              min={0}
              max={100}
              step={5}
              marks
              onChange={this.handleSliderChange}
              valueLabelDisplay="auto"
              color="primary"
            />
          </Box>

          <Button
            className={classes.saveSplitButton}
            variant="contained"
            color="primary"
            onClick={this.handleSaveSplit}
            disabled={!splitChanged || savingPercent}
          >
            {savingPercent ? <CircularProgress size={20} color="inherit" /> : hebrewLabels.saveSplit}
          </Button>
        </Paper>

        <Divider style={{ marginBottom: 32 }} />

        {/* ── Promos list ───────────────────────────────────────────────────── */}
        <Typography variant="h6" className={classes.sectionTitle}>
          {hebrewLabels.promosSection}
        </Typography>

        <Button
          className={classes.addButton}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={this.openCreateDialog}
        >
          {hebrewLabels.addPromo}
        </Button>

        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{hebrewLabels.table.product}</TableCell>
                  <TableCell>{hebrewLabels.table.title}</TableCell>
                  <TableCell>{hebrewLabels.table.cta}</TableCell>
                  <TableCell align="center">{hebrewLabels.table.sortOrder}</TableCell>
                  <TableCell align="center">{hebrewLabels.table.active}</TableCell>
                  <TableCell align="center">{hebrewLabels.table.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {promos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      אין פרומואים עדיין
                    </TableCell>
                  </TableRow>
                ) : (
                  promos.map((promo) => {
                    const product = products.find((p) => p.ean === promo.ean);
                    return (
                      <TableRow key={String(promo._id)} hover>
                        <TableCell>
                          <Box className={classes.productOption}>
                            {product && (
                              <img
                                src={getProductImageUrl(product)}
                                alt={product.displayName}
                                className={classes.productImage}
                              />
                            )}
                            <Box>
                              <Typography className={classes.productName}>
                                {product ? `${product.brand} ${product.displayName}` : promo.ean}
                              </Typography>
                              {product && (
                                <Typography className={classes.productMeta}>
                                  {product.displaySize} · {promo.ean}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>{promo.title}</TableCell>
                        <TableCell>{promo.ctaText || "קנה עכשיו"}</TableCell>
                        <TableCell align="center">{promo.sortOrder}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={promo.active ? hebrewLabels.status.active : hebrewLabels.status.inactive}
                            size="small"
                            className={promo.active ? classes.activeChip : classes.inactiveChip}
                            onClick={() => this.handleToggleActive(promo)}
                            clickable
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => this.openEditDialog(promo)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => this.openDeleteDialog(promo)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* ── Create / Edit dialog ──────────────────────────────────────────── */}
        <Dialog open={dialogOpen} onClose={this.closeDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {dialogMode === "create" ? hebrewLabels.dialog.create : hebrewLabels.dialog.edit}
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>

            {/* Product picker */}
            <Autocomplete
              className={classes.formField}
              options={products}
              loading={loadingProducts}
              getOptionLabel={this.formatProductLabel}
              value={selectedProduct}
              onChange={this.handleProductChange}
              renderOption={(product) => (
                <Box className={classes.productOption}>
                  <img
                    src={getProductImageUrl(product)}
                    alt={product.displayName}
                    className={classes.productImage}
                  />
                  <Box className={classes.productDetails}>
                    <Typography className={classes.productName}>
                      {product.brand} {product.displayName}
                    </Typography>
                    <Typography className={classes.productMeta}>
                      {product.displaySize} · {product.ean} · ₪{product.price}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={hebrewLabels.fields.product}
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProducts && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <TextField
              className={classes.formField}
              label={hebrewLabels.fields.title}
              value={currentPromo.title}
              onChange={(e) => this.handleFieldChange("title", e.target.value)}
              fullWidth
              variant="outlined"
              required
            />

            <TextField
              className={classes.formField}
              label={hebrewLabels.fields.ctaText}
              value={currentPromo.ctaText}
              onChange={(e) => this.handleFieldChange("ctaText", e.target.value)}
              fullWidth
              variant="outlined"
              placeholder="קנה עכשיו"
            />

            <TextField
              className={classes.formField}
              label={hebrewLabels.fields.sortOrder}
              value={currentPromo.sortOrder}
              onChange={(e) => this.handleFieldChange("sortOrder", e.target.value)}
              fullWidth
              variant="outlined"
              type="number"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(currentPromo.active)}
                  onChange={(e) => this.handleFieldChange("active", e.target.checked)}
                  color="primary"
                />
              }
              label={hebrewLabels.fields.active}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeDialog}>{hebrewLabels.buttons.cancel}</Button>
            <Button onClick={this.handleSave} color="primary" variant="contained">
              {hebrewLabels.buttons.save}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Delete confirm ────────────────────────────────────────────────── */}
        <Dialog open={deleteDialogOpen} onClose={() => this.setState({ deleteDialogOpen: false })}>
          <DialogTitle>{hebrewLabels.deleteConfirm}</DialogTitle>
          <DialogActions>
            <Button onClick={() => this.setState({ deleteDialogOpen: false })}>
              {hebrewLabels.buttons.cancel}
            </Button>
            <Button onClick={this.handleDelete} color="secondary" variant="contained">
              {hebrewLabels.buttons.delete}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Snackbar ─────────────────────────────────────────────────────── */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={this.closeSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert onClose={this.closeSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

export default withStyles(styles)(HomepageSalesPromoManager);
